import moment from 'moment';
import pluralize from 'pluralize';
import React, { useContext, useState } from 'react';
import Initial from 'react-initial';
import { useSelector } from 'react-redux';
import showdown from 'showdown';
import styled from 'styled-components';
import tw from 'twin.macro';

import { updatePost } from '../../service/forum';
import { makeSelectIsIcscOrg, makeSelectUser } from '../../store/selectors';
import { getFullPathToS3, notify } from '../../utils/helper';
import NewComment from './NewComment';
import PostComments from './PostComments';
import PostForm from './PostForm';

import { isEnterprise } from '../../config';
import { AppContext } from '../../providers/AppProvider';

const ADMINS = ['Spencer Burton', 'Michael Belasco'];
const Container = styled.div.attrs({
  className: 'mt-6 rounded-15 sm:mt-5',
})`
  &:not(:first-child) {
    ${tw`sm:mt-3`}
  }

  .avatar {
    min-width: 30px;
    min-height: 30px;
    img {
      width: 30px;
      height: 30px;
    }
  }

  .post-title {
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    ${tw`text-primary`}
  }

  @media only screen and (max-width: 639px) {
  }
`;

const Header = styled.div.attrs({
  className: 'flex items-start justify-between w-full',
})`
  .more-button {
    ${tw`w-9 h-9 rounded-full flex items-center cursor-pointer justify-center hover:bg-gray-800 focus:bg-light-gray`}
  }

  .publish-date {
    margin-left: 8px;
    min-width: 70px;
    font-size: 12px;
    line-height: 15px;
    color: rgba(98, 120, 140, 0.4);
  }

  .highlighted {
    background-color: yellow;
  }
`;
const Content = styled.div.attrs({
  className: 'text-primary',
})`
  font-size: 14px;
  font-weight: 300;
  line-height: 15px;

  .media:not(:last-child) {
    margin-bottom: 12px;
  }

  .preview {
    max-height: 75px;
    overflow: hidden;
  }
  .content-overlay {
    height: 50px;
    position: absolute;
    bottom: 0;
    width: 100%;
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.1)
    );
  }

  strong {
    font-weight: bold;
  }

  a {
    color: blue;
    text-decoration: underline;
  }

  iframe {
    max-width: 100%;
  }

  ul {
    list-style: initial;
  }

  .highlighted {
    background-color: yellow;
  }
`;

const Footer = styled.div.attrs({
  className: 'flex justify-start items-center mt-2',
})`
  .author {
    font-size: 12px;
    line-height: 15px;
    color: #e8b12c;
    margin-right: 30px;
  }

  .reply-button {
    ${tw`text-primary flex items-center cursor-pointer`}
    &:hover {
      background: #eee;
      border-radius: 4px;
    }
    font-size: 12px;
    svg {
      margin-right: 6px;
    }
  }
`;

const converter = new showdown.Converter();

const Post = ({
  id,
  parentId,
  _id: postId,
  author,
  title,
  content,
  createdAt: publishedAt,
  comments = [],
  isComment = false,
  files = [],
  isUpdated,
  video,
  isPrivate,
  onAddComment = () => {},
  onDelete,
}) => {
  const currentUser = useSelector(makeSelectUser());
  const [preview, setPreview] = useState(true);
  const [showReply, setShowReply] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState(comments.length);
  const [editing, setEditing] = useState(false);
  const [newContent, setNewContent] = useState(content);
  const [isNewlyUpdated, setIsNewlyUpdated] = useState(isUpdated);
  const isIcsc = useSelector(makeSelectIsIcscOrg());

  const { query, setQuery } = useContext(AppContext);

  const handleComment = async ({ value, video = null, attachments = [] }) => {
    setCommenting(true);

    try {
      const payload = {
        postId,
        content: value,
        video,
        attachments,
      };
      if (isComment) {
        payload.parentId = id;
      }

      onAddComment(payload);

      setComment(comment + 1);
      setCommenting(false);
    } catch (err) {
      setCommenting(false);
      console.log('Error in submitting comment.', err);
      notify(
        'error',
        'An error occurred in submitting comment: ' + (err && err.toString())
      );
    }
  };
  const handleShowComments = () => {
    setShowReply((showReply) => !showReply);
  };

  const handleOpenAttachment = (file) => () => {
    window.open(getFullPathToS3(file), '_blank');
  };

  const handleToggleEditing = () => {
    setEditing(!editing);
  };

  // const handleGoToQuestion = () => {
  //   console.log(parentId);
  //   setQuery(parentId.replace(/-/g, ' '));
  //   console.log(parentId.replace(/-/g, ' '));
  // };

  const handleGoToQuestion = () => {
    if (isComment) {
      console.log('title', title);
      setQuery(title);
    }
  };

  const handleSubmit = async (data) => {
    try {
      await updatePost({
        ...data,
        link: window.location.href,
        content: data.content || data.value,
        type: isIcsc ? 'icsc' : isEnterprise ? 'enterprise' : '',
        id: postId,
      });
      notify(
        'success',
        `Updated ${isComment ? 'comment' : 'post'} successfully!`
      );
      setNewContent(data.content || data.value);
      setIsNewlyUpdated(true);
      setEditing(false);
    } catch (e) {
      console.log('[updatepost.error]', e);
      notify(
        'error',
        `Failed to update ${isComment ? 'comment' : 'post'}: ${
          e && e.toString()
        }`
      );
    }
  };

  return (
    <>
      {editing ? (
        <div className="mt-6 sm:mt-5">
          {isComment ? (
            <NewComment
              user={{ name: currentUser.name }}
              editing={true}
              content={newContent}
              onSubmit={handleSubmit}
              onCancel={handleToggleEditing}
            />
          ) : (
            <PostForm
              defaultValues={{ content: newContent, title }}
              onSubmit={handleSubmit}
              onCancel={handleToggleEditing}
            />
          )}
        </div>
      ) : (
        <Container key={id}>
          <div className="flex items-start w-full">
            <div className="avatar rounded-full overflow-hidden mr-3">
              {author && (
                <Initial
                  width={30}
                  height={30}
                  fontSize={10}
                  circle={true}
                  color={
                    ADMINS.includes(author.name)
                      ? '#EAB22C'
                      : 'rgba(24, 28, 52)'
                  }
                  textColor={ADMINS.includes(author.name) ? 'black' : 'white'}
                  name={author.name}
                />
              )}
            </div>
            <div className="flex-grow">
              <Header>
                <div className="post-info flex flex-grow items-center ">
                  {!isComment && <div className="post-title">{title}</div>}
                  {!isComment && isPrivate && (
                    <div className="text-sm px-1 rounded ml-5 text-white bg-light-yellow">
                      Private
                    </div>
                  )}
                </div>
                <div className="publish-date">
                  {moment(publishedAt).format('MMM DD, YYYY')}
                </div>
              </Header>
              <Content>
                {isComment === true && query && (
                  <>
                    {query !== parentId.replace(/-/g, ' ') ? (
                      <div className="post-info flex flex-grow items-center">
                        <div className="post-title mb-1">
                          {parentId.replace(/-/g, ' ')}
                        </div>
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                )}
                <div
                  // dangerouslySetInnerHTML={{
                  //   __html: converter.makeHtml(
                  //     // preview
                  //     //   ? (content || '').replace(/<p><br><\/p>/g, '')
                  //     query
                  //       ? newContent.replace(
                  //           new RegExp(
                  //             `(${query.replace(
                  //               /[-/\\^$*+?.()|[\]{}]/g,
                  //               '\\$&'
                  //             )})`,
                  //             'gi'
                  //           ),
                  //           '<span class="highlighted">$1</span>'
                  //         )
                  //       : newContent
                  //   ),
                  // }}

                  // dangerouslySetInnerHTML={{
                  //   __html: converter.makeHtml(
                  //     query
                  //       ? (!isComment
                  //           ? title.replace(
                  //               new RegExp(
                  //                 `(${query.replace(
                  //                   /[-/\\^$*+?.()|[\]{}]/g,
                  //                   '\\$&'
                  //                 )})`,
                  //                 'gi'
                  //               ),
                  //               '<span class="highlighted">$1</span>'
                  //             ) + '<br><br>'
                  //           : '') +
                  //           newContent.replace(
                  //             new RegExp(
                  //               `(${query.replace(
                  //                 /[-/\\^$*+?.()|[\]{}]/g,
                  //                 '\\$&'
                  //               )})`,
                  //               'gi'
                  //             ),
                  //             '<span class="highlighted">$1</span>'
                  //           )
                  //       : newContent
                  //   ),
                  // }}
                  dangerouslySetInnerHTML={{
                    __html: converter.makeHtml(
                      query
                        ? (!isComment && title.includes(query)
                            ? title.replace(
                                new RegExp(
                                  `(${query.replace(
                                    /[-/\\^$*+?.()|[\]{}]/g,
                                    '\\$&'
                                  )})`,
                                  'gi'
                                ),
                                '<span class="highlighted">$1</span>'
                              ) + '<br><br>'
                            : '') +
                            newContent.replace(
                              new RegExp(
                                `(${query.replace(
                                  /[-/\\^$*+?.()|[\]{}]/g,
                                  '\\$&'
                                )})`,
                                'gi'
                              ),
                              '<span class="highlighted">$1</span>'
                            )
                        : newContent
                    ),
                  }}
                />
                {video && (
                  <div
                    className="mt-5"
                    dangerouslySetInnerHTML={{
                      __html: video,
                    }}
                  />
                )}
              </Content>
              {files.length > 0 && (
                <div className="attachment-container my-2">
                  {files.map((file) => (
                    <div
                      className="attachment underline cursor-pointer text-sm my-1"
                      key={file}
                      onClick={handleOpenAttachment(file)}
                    >
                      {file}
                    </div>
                  ))}
                </div>
              )}
              <Footer>
                <div className="author cursor-pointer">{author?.name}</div>
                {!isComment && (
                  <div className="flex-grow flex items-center justify-start">
                    <div className="reply-button" onClick={handleShowComments}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.89474 0H10.1053C10.6078 0 11.0897 0.199626 11.445 0.554962C11.8004 0.910298 12 1.39224 12 1.89476V7.57903C12 8.08155 11.8004 8.56349 11.445 8.91883C11.0897 9.27417 10.6078 9.47379 10.1053 9.47379H7.20884L4.86758 11.8151C4.77925 11.9034 4.66673 11.9635 4.54423 11.9879C4.42173 12.0122 4.29477 11.9997 4.17938 11.9519C4.06399 11.9041 3.96536 11.8232 3.89596 11.7194C3.82656 11.6155 3.7895 11.4934 3.78947 11.3685V9.47379H1.89474C1.39222 9.47379 0.910288 9.27417 0.554956 8.91883C0.199624 8.56349 0 8.08155 0 7.57903V1.89476C0 1.39224 0.199624 0.910298 0.554956 0.554962C0.910288 0.199626 1.39222 0 1.89474 0ZM10.1053 0.631586H1.89474C1.55973 0.631586 1.23844 0.76467 1.00155 1.00156C0.764661 1.23845 0.631579 1.55974 0.631579 1.89476V7.57903C0.631579 7.91405 0.764661 8.23534 1.00155 8.47223C1.23844 8.70912 1.55973 8.84221 1.89474 8.84221H4.42105V11.3685L6.94737 8.84221H10.1053C10.4403 8.84221 10.7616 8.70912 10.9985 8.47223C11.2353 8.23534 11.3684 7.91405 11.3684 7.57903V1.89476C11.3684 1.55974 11.2353 1.23845 10.9985 1.00156C10.7616 0.76467 10.4403 0.631586 10.1053 0.631586Z"
                          fill="black"
                        />
                      </svg>
                      {comment > 0
                        ? pluralize('Reply', comment, true)
                        : 'Reply'}
                    </div>
                  </div>
                )}
                {isNewlyUpdated && (
                  <div className="text-gray text-sm mr-5">(Edited)</div>
                )}
                {(currentUser.username === author?.username ||
                  currentUser.role === 'admin' ||
                  currentUser.email === author?.username) && (
                  <>
                    <div
                      className="reply-button mr-3"
                      onClick={handleToggleEditing}
                    >
                      Edit
                    </div>
                    <div
                      className="reply-button mr-3"
                      onClick={onDelete(postId)}
                    >
                      Delete
                    </div>
                  </>
                )}
                {isComment === true &&
                  query &&
                  query !== parentId.replace(/-/g, ' ') && (
                    <span style={{ color: '#30303061', fontSize: '11px' }}>
                      (Reply)
                    </span>
                  )}
              </Footer>
            </div>
          </div>
          {/* <span
            className="my-2 underline cursor-pointer text-gray font-thin text-xs"
            onClick={() => setPreview(!preview)}
          >
            {!preview ? 'Less' : 'Show more'}
          </span> */}
        </Container>
      )}

      {showReply && (
        <>
          <div className="comment-wrapper pl-6 mt-6 mb-15 sm:pl-0 sm:mt-5 sm:mb-5">
            <NewComment
              user={{
                name: currentUser.name,
                role: currentUser.role,
              }}
              busy={commenting}
              onSubmit={handleComment}
            />
          </div>
          <PostComments
            parentId={parentId}
            comments={comments}
            onDelete={onDelete}
          />
        </>
      )}
    </>
  );
};

export default Post;
